/**
 * Z-Score
 *
 * Measures how many standard deviations the current price
 * is from the mean price over a lookback period.
 * Z = (Close − SMA) / StdDev
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA, getSource } from "./sma";

export const zScoreIndicator: IndicatorDefinition = {
  id: "z_score",
  name: "Z-Score",
  shortName: "ZScore",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 5, max: 200 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "HL2", value: "hl2" },
      { label: "HLC3", value: "hlc3" }, { label: "OHLC4", value: "ohlc4" },
    ] },
  ],
  plots: [
    { key: "zscore", label: "Z-Score", color: "#42A5F5", style: "line", lineWidth: 2 },
    { key: "upper", label: "+2", color: "#555555", style: "line", lineWidth: 1 },
    { key: "lower", label: "-2", color: "#555555", style: "line", lineWidth: 1 },
    { key: "zero", label: "Zero", color: "#333333", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source } = params;
    const src = getSource(candles, source);
    const sma = computeSMA(src, period);

    const zData: { time: number; value: number }[] = [];
    const upperData: { time: number; value: number }[] = [];
    const lowerData: { time: number; value: number }[] = [];
    const zeroData: { time: number; value: number }[] = [];

    for (let i = period - 1; i < candles.length; i++) {
      if (sma[i] === undefined) continue;

      let sumSq = 0;
      for (let j = i - period + 1; j <= i; j++) {
        const diff = src[j] - sma[i];
        sumSq += diff * diff;
      }
      const stdDev = Math.sqrt(sumSq / period);

      if (stdDev > 0) {
        const z = (src[i] - sma[i]) / stdDev;
        zData.push({ time: candles[i].time, value: z });
        upperData.push({ time: candles[i].time, value: 2 });
        lowerData.push({ time: candles[i].time, value: -2 });
        zeroData.push({ time: candles[i].time, value: 0 });
      }
    }

    return [
      { key: "zscore", data: zData },
      { key: "upper", data: upperData },
      { key: "lower", data: lowerData },
      { key: "zero", data: zeroData },
    ];
  },
};
